import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import styled from 'styled-components';

import FullScreenLoader from '@/components/common/Loaders/FullScreenLoader';
import { MIXPANEL_ACTIONS } from '@/mixPanel/mixPanelActions';
import useAuthController from '@/pages/Auth/useAuthController';
import GuestRoute from '@/pages/Routing/GuestRoute/GuestRoute';

interface IAuthProps {
    className?: string;
}

const Login = React.lazy(() => import(/* webpackChunkName: "Login" */ '@/pages/Auth/Login/Login'));
const Register = React.lazy(() => import(/* webpackChunkName: "Register" */ '@/pages/Auth/Register/Register'));
const AuthSuccessPage = React.lazy(
    () => import(/* webpackChunkName: "AuthSuccessPage" */ '@/pages/Auth/AuthSuccessPage')
);
const ActivateAccountInvalidToken = React.lazy(
    () => import(/* webpackChunkName: "ActivateAccountInvalidToken" */ '@/pages/Auth/ActivateAccountInvalidToken')
);
const UserActivateAccount = React.lazy(
    () => import(/* webpackChunkName: "UserActivateAccount" */ '@/pages/Auth/UserActivateAccount/UserActivateAccount')
);
const ResetPasswordFirstStep = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ResetPasswordFirstStep" */ '@/pages/Auth/ResetPassword/ResetPasswordFirstStep/ResetPasswordFirstStep'
        )
);
const ResetPasswordSecondStep = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ResetPasswordSecondStep" */ '@/pages/Auth/ResetPassword/ResetPasswordSecondStep/ResetPasswordSecondStep'
        )
);

const ResetPasswordInvalidToken = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ResetPasswordInvalidToken" */ '@/pages/Auth/ResetPassword/ResetPasswordInvalidToken'
        )
);

export enum AUTH_ROUTES {
    LOGIN = '/login',
    ORGANIZATION_ACTIVATE_ACCOUNT_SUCCESS = '/organization-activate-account/success',
    ORGANIZATION_ACTIVATE_ACCOUNT_INVALID_TOKEN = '/organization-activate-account/invalid-token',
    USER_ACTIVATE_ACCOUNT = '/user-activate-account',
    USER_ACTIVATE_ACCOUNT_SUCCESS = '/user-activate-account/success',
    USER_ACTIVATE_ACCOUNT_INVALID_TOKEN = '/user-activate-account/invalid-token',
    RESET_PASSWORD_FIRST_STEP = '/reset-password-first-step',
    RESET_PASSWORD_SECOND_STEP = '/reset-password-second-step',
    RESET_PASSWORD_INVALID_TOKEN = '/reset-password-invalid-token',
    REGISTER = '/register',
    REGISTER_ORGANIZATION_AND_USER = '/register-organization-and-user',
    FORGET_PASSWORD = '/forget-password',
    STRIPE_STATUS = '/stripe-status',
}

export type AuthController = ReturnType<typeof useAuthController> | null;
export const AuthContext = React.createContext<AuthController>(null);

const Auth = ({ className }: IAuthProps) => {
    const AuthController = useAuthController();
    const { t } = useTranslation();
    return (
        <div className={className}>
            {AuthController.isLoading ? <FullScreenLoader /> : null}
            <AuthContext.Provider value={AuthController}>
                <Routes>
                    <Route path={AUTH_ROUTES.REGISTER} element={<Register />} />
                    <Route
                        path={AUTH_ROUTES.ORGANIZATION_ACTIVATE_ACCOUNT_SUCCESS}
                        element={<AuthSuccessPage ctaLabel={t('loginView_loginLabel_text')} />}
                    />
                    <Route
                        path={AUTH_ROUTES.ORGANIZATION_ACTIVATE_ACCOUNT_INVALID_TOKEN}
                        element={<ActivateAccountInvalidToken />}
                    />
                    <Route
                        path={AUTH_ROUTES.LOGIN}
                        element={
                            <GuestRoute>
                                <Login />
                            </GuestRoute>
                        }
                    />
                    <Route path={AUTH_ROUTES.USER_ACTIVATE_ACCOUNT} element={<UserActivateAccount />} />
                    <Route
                        path={AUTH_ROUTES.USER_ACTIVATE_ACCOUNT_SUCCESS}
                        element={<AuthSuccessPage mixpanelAction={MIXPANEL_ACTIONS.NEW_EMPLOYEE_ACCOUNT_ACTIVATED} />}
                    />
                    <Route
                        path={AUTH_ROUTES.USER_ACTIVATE_ACCOUNT_INVALID_TOKEN}
                        element={<ActivateAccountInvalidToken />}
                    />
                    <Route
                        path={AUTH_ROUTES.RESET_PASSWORD_FIRST_STEP}
                        element={
                            <GuestRoute>
                                <ResetPasswordFirstStep />
                            </GuestRoute>
                        }
                    />
                    <Route path={AUTH_ROUTES.RESET_PASSWORD_SECOND_STEP} element={<ResetPasswordSecondStep />} />
                    <Route path={AUTH_ROUTES.RESET_PASSWORD_INVALID_TOKEN} element={<ResetPasswordInvalidToken />} />
                </Routes>
            </AuthContext.Provider>
        </div>
    );
};

const Memoized = memo(Auth);
const Styled = styled(Memoized)`
    & .MuiGrid-root {
        min-height: 100vh;
    }
`;
export default Styled;
